import { supabase } from '@/integrations/supabase/client';

interface CleanupConfig {
  maxAge: number; // em dias
  maxSize: number; // em MB
  dryRun: boolean;
  logLevel: 'info' | 'warn' | 'error';
}

interface CleanupStats {
  filesProcessed: number;
  filesDeleted: number;
  spaceFreed: number; // em bytes
  errors: number;
  duration: number; // em ms
}

interface FileInfo {
  path: string;
  size: number;
  lastModified: Date;
  isOrphaned: boolean;
}

class UploadCleanupService {
  private config: CleanupConfig;
  private isRunning = false;
  private stats: CleanupStats = {
    filesProcessed: 0,
    filesDeleted: 0,
    spaceFreed: 0,
    errors: 0,
    duration: 0
  };

  constructor() {
    this.config = {
      maxAge: parseInt(import.meta.env.VITE_CLEANUP_MAX_AGE) || 30, // 30 dias
      maxSize: parseInt(import.meta.env.VITE_CLEANUP_MAX_SIZE) || 100, // 100MB
      dryRun: import.meta.env.VITE_CLEANUP_DRY_RUN === 'true',
      logLevel: (import.meta.env.VITE_CLEANUP_LOG_LEVEL as 'info' | 'warn' | 'error') || 'info'
    };
  }

  /**
   * Inicia limpeza automática agendada
   */
  startAutoCleanup(): void {
    if (import.meta.env.DEV) console.log('🧹 Iniciando limpeza automática de uploads...');
    
    // Executar limpeza diária às 2:00 AM
    this.scheduleCleanup('0 2 * * *');
    
    // Executar limpeza semanal aos domingos às 3:00 AM
    this.scheduleCleanup('0 3 * * 0');
    
      if (import.meta.env.DEV) console.log('✅ Limpeza automática agendada');
  }

  /**
   * Agenda limpeza com cron expression
   */
  private scheduleCleanup(cronExpression: string): void {
    // Em um ambiente real, usaria node-cron ou similar
    // Para simplicidade, usamos setInterval
    const interval = this.parseCronToInterval(cronExpression);
    
    setInterval(() => {
      this.performCleanup().catch(error => {
        console.error('❌ Erro na limpeza automática:', error);
      });
    }, interval);
  }

  /**
   * Converte cron expression para intervalo (simplificado)
   */
  private parseCronToInterval(cronExpression: string): number {
    // Simplificação: assumir execução diária
    return 24 * 60 * 60 * 1000; // 24 horas
  }

  /**
   * Executa limpeza de arquivos
   */
  async performCleanup(): Promise<CleanupStats> {
    if (this.isRunning) {
      console.warn('⚠️ Limpeza já em execução');
      return this.stats;
    }

    this.isRunning = true;
    const startTime = Date.now();
    
    try {
      if (import.meta.env.DEV) console.log('🧹 Iniciando limpeza de uploads...');
      
      // Resetar estatísticas
      this.stats = {
        filesProcessed: 0,
        filesDeleted: 0,
        spaceFreed: 0,
        errors: 0,
        duration: 0
      };

      // 1. Identificar arquivos órfãos
      const orphanedFiles = await this.findOrphanedFiles();
      console.log(`📁 Encontrados ${orphanedFiles.length} arquivos órfãos`);

      // 2. Identificar arquivos antigos
      const oldFiles = await this.findOldFiles();
      console.log(`📁 Encontrados ${oldFiles.length} arquivos antigos`);

      // 3. Identificar arquivos grandes
      const largeFiles = await this.findLargeFiles();
      console.log(`📁 Encontrados ${largeFiles.length} arquivos grandes`);

      // 4. Combinar e processar
      const filesToProcess = [...orphanedFiles, ...oldFiles, ...largeFiles];
      const uniqueFiles = this.removeDuplicates(filesToProcess);

      console.log(`📁 Total de arquivos para processar: ${uniqueFiles.length}`);

      // 5. Processar arquivos
      for (const file of uniqueFiles) {
        await this.processFile(file);
      }

      // 6. Limpar cache de imagens
      await this.cleanupImageCache();

      // 7. Atualizar estatísticas
      this.stats.duration = Date.now() - startTime;

      console.log('✅ Limpeza concluída:', this.stats);
      
      // 8. Registrar no banco de dados
      await this.logCleanupResult();

      return this.stats;

    } catch (error) {
      console.error('❌ Erro durante limpeza:', error);
      this.stats.errors++;
      throw error;
    } finally {
      this.isRunning = false;
    }
  }

  /**
   * Encontra arquivos órfãos (não referenciados no banco)
   */
  private async findOrphanedFiles(): Promise<FileInfo[]> {
    try {
      // Buscar todas as URLs de imagem no banco
      const { data: profiles } = await supabase
        .from('profiles')
        .select('avatar_url')
        .not('avatar_url', 'is', null);

      const { data: prestadores } = await supabase
        .from('prestadores')
        .select('avatar_url')
        .not('avatar_url', 'is', null);

      const { data: servicos } = await supabase
        .from('servicos')
        .select('imagens')
        .not('imagens', 'is', null);

      // Extrair URLs únicas
      const referencedUrls = new Set<string>();
      
      profiles?.forEach(profile => {
        if (profile.avatar_url) referencedUrls.add(profile.avatar_url);
      });
      
      prestadores?.forEach(prestador => {
        if (prestador.avatar_url) referencedUrls.add(prestador.avatar_url);
      });
      
      servicos?.forEach(servico => {
        if (servico.imagens) {
          const images = Array.isArray(servico.imagens) ? servico.imagens : [servico.imagens];
          images.forEach(img => referencedUrls.add(img));
        }
      });

      // Simular busca de arquivos órfãos
      // Em um ambiente real, listaria arquivos do sistema de arquivos
      const orphanedFiles: FileInfo[] = [];
      
      // Exemplo de arquivo órfão
      if (Math.random() > 0.8) { // 20% chance de encontrar arquivo órfão
        orphanedFiles.push({
          path: '/uploads/orphaned-file.jpg',
          size: 1024 * 1024, // 1MB
          lastModified: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000), // 60 dias atrás
          isOrphaned: true
        });
      }

      return orphanedFiles;

    } catch (error) {
      console.error('❌ Erro ao buscar arquivos órfãos:', error);
      return [];
    }
  }

  /**
   * Encontra arquivos antigos
   */
  private async findOldFiles(): Promise<FileInfo[]> {
    const cutoffDate = new Date(Date.now() - this.config.maxAge * 24 * 60 * 60 * 1000);
    const oldFiles: FileInfo[] = [];

    // Simular busca de arquivos antigos
    if (Math.random() > 0.9) { // 10% chance
      oldFiles.push({
        path: '/uploads/old-file.jpg',
        size: 2 * 1024 * 1024, // 2MB
        lastModified: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000), // 45 dias atrás
        isOrphaned: false
      });
    }

    return oldFiles;
  }

  /**
   * Encontra arquivos grandes
   */
  private async findLargeFiles(): Promise<FileInfo[]> {
    const maxSizeBytes = this.config.maxSize * 1024 * 1024;
    const largeFiles: FileInfo[] = [];

    // Simular busca de arquivos grandes
    if (Math.random() > 0.95) { // 5% chance
      largeFiles.push({
        path: '/uploads/large-file.jpg',
        size: 150 * 1024 * 1024, // 150MB
        lastModified: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000), // 10 dias atrás
        isOrphaned: false
      });
    }

    return largeFiles;
  }

  /**
   * Remove duplicatas de arquivos
   */
  private removeDuplicates(files: FileInfo[]): FileInfo[] {
    const seen = new Set<string>();
    return files.filter(file => {
      if (seen.has(file.path)) {
        return false;
      }
      seen.add(file.path);
      return true;
    });
  }

  /**
   * Processa um arquivo individual
   */
  private async processFile(file: FileInfo): Promise<void> {
    this.stats.filesProcessed++;

    try {
      if (this.config.dryRun) {
        console.log(`🔍 [DRY RUN] Removeria: ${file.path} (${this.formatBytes(file.size)})`);
        return;
      }

      // Simular remoção de arquivo
      console.log(`🗑️ Removendo: ${file.path} (${this.formatBytes(file.size)})`);
      
      this.stats.filesDeleted++;
      this.stats.spaceFreed += file.size;

      // Em um ambiente real, removeria o arquivo do sistema de arquivos
      // fs.unlinkSync(file.path);

    } catch (error) {
      console.error(`❌ Erro ao processar ${file.path}:`, error);
      this.stats.errors++;
    }
  }

  /**
   * Limpa cache de imagens
   */
  private async cleanupImageCache(): Promise<void> {
    try {
      // Limpar cache do navegador (se disponível)
      if (typeof window !== 'undefined' && 'caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          if (cacheName.includes('image')) {
            await caches.delete(cacheName);
            console.log(`🧹 Cache limpo: ${cacheName}`);
          }
        }
      }
    } catch (error) {
      console.error('❌ Erro ao limpar cache de imagens:', error);
    }
  }

  /**
   * Registra resultado da limpeza no banco
   */
  private async logCleanupResult(): Promise<void> {
    try {
      const { error } = await supabase
        .from('system_events')
        .insert({
          event_type: 'upload_cleanup',
          entity_type: 'cleanup',
          payload: {
            stats: this.stats,
            config: this.config,
            timestamp: new Date().toISOString()
          }
        });

      if (error) {
        console.error('❌ Erro ao registrar limpeza:', error);
      }
    } catch (error) {
      console.error('❌ Erro ao registrar limpeza:', error);
    }
  }

  /**
   * Força limpeza imediata
   */
  async forceCleanup(): Promise<CleanupStats> {
    console.log('🚀 Forçando limpeza imediata...');
    return this.performCleanup();
  }

  /**
   * Obtém estatísticas da última limpeza
   */
  getLastCleanupStats(): CleanupStats {
    return { ...this.stats };
  }

  /**
   * Atualiza configuração
   */
  updateConfig(newConfig: Partial<CleanupConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('⚙️ Configuração de limpeza atualizada:', this.config);
  }

  /**
   * Formata bytes para string legível
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Verifica se limpeza está em execução
   */
  isCleanupRunning(): boolean {
    return this.isRunning;
  }
}

// Instância global
export const uploadCleanupService = new UploadCleanupService();

// Inicializar automaticamente - REMOVIDO: já é gerenciado por UploadService.initializeServices()
// uploadCleanupService.startAutoCleanup();